import "./App.css";
import React from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Header from "./components/Header";
import Footer from "./components/Footer";
import GameCards from "./components/GameCards";
import BackgroundSvg from "./components/BackgroundSVG";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import BuySubscriptionPage from "./pages/BuySubscriptionPage";
import AccountPage from "./pages/AccountPage";
import AboutPage from "./pages/AboutPage";
import ContactPage from "./pages/ContactPage";
import GamePlayer from "./components/GamePlayer";
import TournamentListPage from "./pages/TournamentListPage";
import TournamentBuilderPage from "./pages/TournamentBuilderPage";
import TournamentRunPage from "./pages/TournamentRunPage";
import { useBootstrapCsrf } from "./api/api_hooks";
import { RemoteControlProvider } from "./remote/RemoteControlContext";
import RemoteControllerPage from "./remote/RemoteControllerPage";
import SurveyPage from "./pages/SurveyPage";

const heroSlides = [
  {
    image: "/hero_img.png",
    alt: "Colorful gaming illustration",
    title: "Games for EVERYONE!",
    subtitle: "Use your phone as a controller! No downloads, no hassle. Just instant fun with your friends!",
  },
  {
    image: "/hero_img_2.png",
    alt: "Active gaming experience",
    title: "Move, Play, Compete!",
    subtitle: "Use your phone as a kinect! Jump, walk, and compete in a variety of fun and active games!",
  },
];

function Home() {
  const [activeSlide, setActiveSlide] = React.useState(0);
  const slideTimerRef = React.useRef(null);
  const currentSlide = heroSlides[activeSlide];

  const clearSlideTimer = React.useCallback(() => {
    if (slideTimerRef.current) {
      window.clearInterval(slideTimerRef.current);
      slideTimerRef.current = null;
    }
  }, []);

  const startSlideTimer = React.useCallback(() => {
    clearSlideTimer();
    slideTimerRef.current = window.setInterval(() => {
      setActiveSlide((current) => (current + 1) % heroSlides.length);
    }, 5000);
  }, [clearSlideTimer]);

  React.useEffect(() => {
    startSlideTimer();
    return clearSlideTimer;
  }, [clearSlideTimer, startSlideTimer]);

  const handleSlideSelect = React.useCallback(
    (index) => {
      setActiveSlide(index);
      startSlideTimer();
    },
    [startSlideTimer]
  );

  return (
    <>
      <div className="relative w-full overflow-hidden md:border-white/10 md:bg-white/5 md:shadow-2xl md:shadow-fuchsia-500/20 md:ring-1 md:ring-white/10">
        <div className="relative h-[430px] w-full lg:h-[590px]">
          {heroSlides.map((slide, index) => (
            <img
              key={slide.image}
              src={slide.image}
              alt={slide.alt}
              className={`block h-full w-full object-cover transition-opacity duration-700 ${
                index === activeSlide ? "opacity-100" : "absolute inset-0 opacity-0"
              }`}
              loading={index === 0 ? "eager" : "lazy"}
            />
          ))}
        </div>

        <div className="absolute inset-x-0 top-0 z-10 flex justify-center px-4 pt-2 text-center sm:pt-2">
          <div className="max-w-3xl">
            <h1 className="text-3xl font-black tracking-[0.05em] text-purple-100 drop-shadow-lg sm:text-4xl md:text-5xl">
              {currentSlide.title}
            </h1>
            <p className="mt-3 text-sm font-medium text-indigo-100/85 drop-shadow sm:text-base md:text-lg">
              {currentSlide.subtitle}
            </p>
          </div>
        </div>

        <div className="absolute inset-x-0 bottom-0 z-10 flex justify-center gap-3 p-4">
          {heroSlides.map((slide, index) => (
            <button
              key={slide.image}
              type="button"
              className={`h-3 w-3 rounded-full border border-white/70 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pink-300 ${
                index === activeSlide ? "bg-white" : "bg-white/25 hover:bg-white/60"
              }`}
              aria-label={`Show slide ${index + 1}`}
              aria-current={index === activeSlide ? "true" : undefined}
              onClick={() => handleSlideSelect(index)}
            />
          ))}
        </div>
      </div>
      <main className="flex-grow">
        <section className="mx-auto flex max-w-6xl flex-col items-center px-4 pb-20 text-center">
          <GameCards remoteHero={{
            title: currentSlide.title,
            activeSlide,
            previous: () => handleSlideSelect((activeSlide - 1 + heroSlides.length) % heroSlides.length),
            next: () => handleSlideSelect((activeSlide + 1) % heroSlides.length),
          }} />
        </section>
      </main>
    </>
  );
}

function WebsiteShell() {
  return (
    <RemoteControlProvider>
      <div className="relative z-10 flex min-h-screen flex-col">
        <Header />
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/subscriptions" element={<BuySubscriptionPage />} />
          <Route path="/account" element={<AccountPage />} />
          <Route path="/survey" element={<SurveyPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/play/:game" element={<GamePlayer />} />
          <Route path="/tournaments" element={<TournamentListPage />} />
          <Route path="/tournaments/new" element={<TournamentBuilderPage />} />
          <Route path="/tournaments/:tournamentId/edit" element={<TournamentBuilderPage />} />
          <Route path="/tournament-runs/:runId" element={<TournamentRunPage />} />
        </Routes>
        <Footer />
      </div>
    </RemoteControlProvider>
  );
}

function RoutedApp() {
  const location = useLocation();
  if (location.pathname.startsWith("/remote/")) {
    return (
      <Routes>
        <Route path="/remote/pair/:pairToken" element={<RemoteControllerPage />} />
        <Route path="/remote/controller" element={<RemoteControllerPage />} />
      </Routes>
    );
  }
  return (
    <>
      <BackgroundSvg />
      <WebsiteShell />
    </>
  );
}

function App() {
  // Set CSRF cookie once so POSTs work
  useBootstrapCsrf();

  return (
    <div className="App relative min-h-screen overflow-x-hidden bg-slate-950 text-white">
      <BrowserRouter>
        <RoutedApp />
      </BrowserRouter>
    </div>
  );
}

export default App;
